import SupportPage from "./Pages/SupportPage.jsx";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import SupportChatLauncher from "./Components/SupportChat/SupportChatLauncher";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import FarmFormPage from "./Pages/FarmFormPage";
import FarmPage from "./Pages/FarmPage";
import FieldFormPage from "./Pages/FieldFormPage";
import CropYearPage from "./Pages/CropYearPage";
import ReviewPage from "./Pages/ReviewPage";
import AnalysisPage from "./Pages/AnalysisPage";
import DashBoardPage from "./Pages/DashBoardPage";
import ProfilePage from "./Pages/ProfilePage";
import ProjectsPage from "./Pages/ProjectsPage.jsx";
import UtilitiesPage from "./Pages/UtilitiesPage.jsx";
import EquipmentPage from "./Pages/EquipmentPage.jsx";
import FertilizersPage from "./Pages/FertilizersPage.jsx";
import ExcelExchangePage from "./Pages/ExcelExchangePage.jsx";
import { LoginPage } from "./Pages/LoginPage.jsx";
import NotFound from "./Pages/NotFoundPage.jsx";
import { AuthProvider, useAuth } from "./Components/Auth/useAuth";
import FieldPage from "./Pages/FieldPage";
import PortalDashboardPage from "./Pages/PortalDashboardPage.jsx";
import AggregatorGrowersPage from "./Pages/AggregatorGrowersPage.jsx";
import WorkforceProfilePage from "./Pages/WorkforceProfilePage.jsx";
import AdminImpersonationPage from "./Pages/AdminImpersonationPage.jsx";
import AdminWorkforceProvisioningPage from "./Pages/AdminWorkforceProvisioningPage.jsx";
import AdminProjectEnrollmentsPage from "./Pages/AdminProjectEnrollmentsPage.jsx";
import PartnerProjectsPage from "./Pages/PartnerProjectsPage.jsx";
import AggregatorReconciliationPage from "./Pages/AggregatorReconciliationPage.jsx";
import {
  resolvePortalRedirect,
  getPortalLabel,
  getPortalDefaultPath,
  pathBelongsToPortal,
  PORTAL_TYPES,
} from "./portal/portalUtils.js";
import {
  getAnalyticsSettings,
  getRouteTemplate,
  setAnalyticsUserId,
  setAnalyticsContext,
  shouldTrackPageView,
  trackEvent,
  trackPageView,
} from "./analytics/ga4.js";
import { axiosInstance } from "./Components/axiosInstance.js";
import SessionRecoveryNotice from "./Components/SessionRecoveryNotice.jsx";
import SessionWorkspaceBoundary from "./Components/Auth/SessionWorkspaceBoundary.jsx";

function normalizeCurrentUserPayload(payload) {
  const nextUser = payload?.user || payload;

  if (!nextUser || typeof nextUser !== "object") {
    return null;
  }

  return nextUser;
}

function readCurrentUserPublicId(currentUser, viewer) {
  return (
    currentUser?.public_id ||
    currentUser?.publicId ||
    viewer?.public_id ||
    viewer?.publicId ||
    null
  );
}

function readCurrentUserAccountType(currentUser, viewer) {
  return (
    currentUser?.account_type ||
    currentUser?.accountType ||
    viewer?.account_type ||
    viewer?.accountType ||
    null
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#F15D22",
      dark: "#B93F13",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#FFFFFF",
    },
    white: {
      main: "#AAAAAA",
    },
    mainOrange: {
      main: "rgb(241,93,34)",
    },
  },
  typography: {
    fontFamily: [
      "Source Sans Pro",
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 700,
          minHeight: 40,
          textTransform: "none",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
  },
});

function LoadingScreen({ message = "Loading..." }) {
  return (
    <div className="w-screen h-screen flex items-center justify-center text-[#666666] text-xl">
      {message}
    </div>
  );
}

function ViewerErrorScreen() {
  const { logout } = useAuth();

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-[#f6f6f6] p-6">
      <div className="max-w-[640px] bg-white border border-[#d6d6d6] p-8 text-[#666666]">
        <h1 className="text-2xl text-[#243245] mb-4">
          We couldn&apos;t load your portal session
        </h1>
        <p className="mb-6">
          Your portal session needs attention. Retry the connection or sign in again below.
        </p>
        <SessionRecoveryNotice />
        <button
          className="bg-[rgb(241,93,34)] text-white px-4 py-2"
          onClick={logout}
          type="button"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

function PortalRedirect({ redirect }) {
  useEffect(() => {
    window.location.replace(redirect.url);
  }, [redirect]);

  return (
    <LoadingScreen
      message={`Redirecting to the ${getPortalLabel(redirect.portal)} portal...`}
    />
  );
}

function GrowerPortalRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/projects/:projectPublicId" element={<ProjectsPage />} />
      <Route path="/utilities" element={<UtilitiesPage />} />
      <Route path="/utilities/:utilityId" element={<UtilitiesPage />} />
      <Route path="/equipment" element={<EquipmentPage />} />
      <Route path="/equipment/:machineId" element={<EquipmentPage />} />
      <Route path="/fertilizers" element={<FertilizersPage />} />
      <Route path="/fertilizers/:mixId" element={<FertilizersPage />} />
      <Route path="/excel" element={<ExcelExchangePage />} />
      <Route path="/dashboard" element={<DashBoardPage />} />
      <Route path="/farm/form" element={<FarmFormPage />} />
      <Route path="/farm/:farmId" element={<FarmPage />} />
      <Route path="/farm/:farmId/form" element={<FarmFormPage />} />
      <Route path="/farm/:farmId/field/form" element={<FieldFormPage />} />
      <Route path="/farm/:farmId/field/:fieldId" element={<FieldPage />} />
      <Route path="/farm/:farmId/field/:fieldId/form" element={<FieldFormPage />} />
      <Route path="/addfarm" element={<FarmFormPage />} />
      <Route path="/farm/:farmId/addfield" element={<FieldFormPage />} />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear"
        element={<CropYearPage />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/addcropyear"
        element={<CropYearPage />}
      />
      <Route path="/farm/:farmId/editfarm" element={<FarmFormPage />} />
      <Route
        path="/farm/:farmId/field/:fieldId/editfield"
        element={<FieldFormPage />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/editcropyear"
        element={<CropYearPage />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/*"
        element={<CropYearPage />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/addanalysis"
        element={<ReviewPage />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/analysis"
        element={<AnalysisPage />}
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function PartnerPortalRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<PortalDashboardPage portal="partner" />} />
      <Route
        path="/projects"
        element={<PartnerProjectsPage />}
      />
      <Route path="/projects/new" element={<PartnerProjectsPage mode="create" />} />
      <Route path="/projects/:projectPublicId" element={<PartnerProjectsPage />} />
      <Route path="/projects/:projectPublicId/edit" element={<PartnerProjectsPage mode="edit" />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/profile" element={<WorkforceProfilePage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AggregatorPortalRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/growers" replace />} />
      <Route
        path="/dashboard"
        element={<DashBoardPage requireSelectedGrower />}
      />
      <Route
        path="/growers"
        element={<AggregatorGrowersPage />}
      />
      <Route
        path="/delegations"
        element={<Navigate to="/growers" replace />}
      />
      <Route
        path="/imports"
        element={<Navigate to="/excel" replace />}
      />
      <Route path="/excel" element={<ExcelExchangePage requireSelectedGrower />} />
      <Route path="/reconciliation" element={<AggregatorReconciliationPage />} />
      <Route path="/farm/:farmId" element={<FarmPage requireSelectedGrower />} />
      <Route path="/farm/:farmId/form" element={<FarmFormPage />} />
      <Route path="/farm/:farmId/field/form" element={<FieldFormPage />} />
      <Route path="/farm/:farmId/field/:fieldId" element={<FieldPage requireSelectedGrower />} />
      <Route path="/farm/:farmId/field/:fieldId/form" element={<FieldFormPage />} />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear"
        element={<CropYearPage requireSelectedGrower />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/addcropyear"
        element={<CropYearPage requireSelectedGrower />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/editcropyear"
        element={<CropYearPage requireSelectedGrower />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/*"
        element={<CropYearPage requireSelectedGrower />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/addanalysis"
        element={<ReviewPage requireSelectedGrower />}
      />
      <Route
        path="/farm/:farmId/field/:fieldId/cropyear/:cropyearId/analysis"
        element={<AnalysisPage requireSelectedGrower />}
      />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/profile" element={<WorkforceProfilePage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AdminPortalRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<PortalDashboardPage portal="admin" />} />
      <Route
        path="/persona"
        element={<AdminImpersonationPage />}
      />
      <Route
        path="/workforce"
        element={<AdminWorkforceProvisioningPage />}
      />
      <Route path="/project-enrollments" element={<AdminProjectEnrollmentsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/profile" element={<WorkforceProfilePage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export function PortalGate() {
  const location = useLocation();
  const {
    viewer,
    viewerError,
    hostPortal,
    activePortal,
    isPortalSimulated,
  } = useAuth();

  if (viewerError && !viewer) {
    return <ViewerErrorScreen />;
  }

  if (!viewer) {
    return <LoadingScreen message="Loading portal workspace..." />;
  }

  const redirect = !isPortalSimulated && resolvePortalRedirect({
    viewer,
    hostPortal,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  if (redirect) {
    return <PortalRedirect redirect={redirect} />;
  }

  // A refresh may leave a path from the simulated role in the address bar.
  // Restore a valid route for the deployed role without leaving this origin.
  if (!pathBelongsToPortal(activePortal, location.pathname) &&
      PORTAL_TYPES.some(portal => pathBelongsToPortal(portal, location.pathname))) {
    return <Navigate to={getPortalDefaultPath(activePortal)} replace />;
  }

  if (activePortal === "partner") {
    return <PartnerPortalRoutes />;
  }

  if (activePortal === "aggregator") {
    return <AggregatorPortalRoutes />;
  }

  if (activePortal === "admin") {
    return <AdminPortalRoutes />;
  }

  return <GrowerPortalRoutes />;
}

function AnalyticsTracker() {
  const location = useLocation();
  const {
    hostPortal,
    activePortal,
    isAuthenticated,
    impersonatedEmail,
    isImpersonating,
    personaPortal,
    viewer,
    viewerLoading,
  } = useAuth();
  const lastTrackedLocationRef = useRef("");
  const [analyticsUser, setAnalyticsUser] = useState(null);
  const [analyticsUserLoading, setAnalyticsUserLoading] = useState(false);
  const analyticsEnabled = getAnalyticsSettings().enabled;
  const routeTemplate = getRouteTemplate(location.pathname, activePortal);
  const analyticsUserId = readCurrentUserPublicId(analyticsUser, viewer);
  const analyticsAccountType = readCurrentUserAccountType(analyticsUser, viewer);

  useEffect(() => {
    let isMounted = true;

    const loadAnalyticsUser = async () => {
      if (!analyticsEnabled) {
        if (isMounted) {
          setAnalyticsUser(null);
          setAnalyticsUserLoading(false);
        }
        return;
      }

      if (!isAuthenticated) {
        if (isMounted) {
          setAnalyticsUser(null);
          setAnalyticsUserLoading(false);
        }
        return;
      }

      if (viewerLoading || !viewer) {
        if (isMounted) {
          setAnalyticsUserLoading(true);
        }
        return;
      }

      if (isMounted) {
        setAnalyticsUserLoading(true);
      }

      try {
        const response = await axiosInstance.get("/users/me");

        if (isMounted) {
          setAnalyticsUser(normalizeCurrentUserPayload(response.data));
        }
      } catch (error) {
        console.error("[analytics] Failed to load /users/me for GA identity", error);

        if (isMounted) {
          setAnalyticsUser(null);
        }
      } finally {
        if (isMounted) {
          setAnalyticsUserLoading(false);
        }
      }
    };

    loadAnalyticsUser();

    return () => {
      isMounted = false;
    };
  }, [analyticsEnabled, impersonatedEmail, isAuthenticated, viewer, viewerLoading]);

  useEffect(() => {
    setAnalyticsContext({
      account_type: analyticsAccountType,
      host_portal: hostPortal,
      is_impersonating: isImpersonating,
      persona_portal: personaPortal,
      is_authenticated: isAuthenticated,
    });
  }, [
    analyticsAccountType,
    hostPortal,
    isAuthenticated,
    isImpersonating,
    personaPortal,
  ]);

  useEffect(() => {
    if (!analyticsEnabled) {
      return;
    }

    if (!isAuthenticated) {
      setAnalyticsUserId(null);
      return;
    }

    if (viewerLoading || analyticsUserLoading) {
      return;
    }

    setAnalyticsUserId(analyticsUserId || null);
  }, [
    analyticsEnabled,
    analyticsUserId,
    analyticsUserLoading,
    isAuthenticated,
    viewerLoading,
  ]);

  useEffect(() => {
    const canTrackAuthenticatedRoute =
      !isAuthenticated ||
      (Boolean(viewer) &&
        !viewerLoading &&
        (!analyticsEnabled || !analyticsUserLoading));

    if (!shouldTrackPageView(location.pathname) || !canTrackAuthenticatedRoute) {
      return;
    }

    const currentLocationKey = `${location.pathname}${location.search}${location.hash}`;

    if (lastTrackedLocationRef.current === currentLocationKey) {
      return;
    }

    trackPageView({
      routeTemplate,
      hostPortal,
      is_impersonating: isImpersonating,
      personaPortal,
      isAuthenticated,
    });

    if (routeTemplate === "dashboard" || routeTemplate === "portal_dashboard") {
      trackEvent("dashboard_viewed", {
        workflow_area: "dashboard",
      });
    }

    if (routeTemplate === "analysis") {
      trackEvent("analysis_viewed", {
        workflow_area: "analysis",
      });
    }

    lastTrackedLocationRef.current = currentLocationKey;
  }, [
    hostPortal,
    isAuthenticated,
    isImpersonating,
    location.hash,
    location.pathname,
    location.search,
    personaPortal,
    routeTemplate,
    viewer,
    viewerLoading,
    analyticsEnabled,
    analyticsUserLoading,
  ]);

  return null;
}

function AppRoutes() {
  const { isAuthenticated, hasWorkspace } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invitation" element={<Navigate to="/login" replace />} />
      <Route path="/registration" element={<Navigate to="/login" replace />} />
      <Route
        path="/forgot_password"
        element={<Navigate to="/login" replace />}
      />
      <Route
        path="/password_reset"
        element={<Navigate to="/login" replace />}
      />

      {!isAuthenticated && !hasWorkspace && (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}

      {(isAuthenticated || hasWorkspace) && <Route path="*" element={<PortalGate />} />}
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <AuthProvider>
          <SessionWorkspaceBoundary loading={<LoadingScreen message="Finishing sign-in..." />}>
          <AnalyticsTracker />
          <AppRoutes />
          <SupportChatLauncher />
          </SessionWorkspaceBoundary>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
